// モザイク設定編集フォーム(設計書 §3.5.3)

import { useState } from 'react';
import { ApiClientError } from '../../api/client';
import {
  useUpdateMosaicSettings,
  type MosaicSettings,
} from '../../api/admin/performers';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';

interface MosaicSettingsFormProps {
  ulid: string;
  initial: MosaicSettings;
  onClose: () => void;
}

export function MosaicSettingsForm({ ulid, initial, onClose }: MosaicSettingsFormProps) {
  const updateMutation = useUpdateMosaicSettings();
  const [targetGenital, setTargetGenital] = useState(initial.mosaic_target_genital);
  const [targetBreast, setTargetBreast] = useState(initial.mosaic_target_breast);
  const [detectionModel, setDetectionModel] = useState(initial.detection_model);
  const [trainingDataDir, setTrainingDataDir] = useState(initial.training_data_dir ?? '');
  const [outputWidth, setOutputWidth] = useState(initial.output_width);
  const [outputHeight, setOutputHeight] = useState(initial.output_height);
  const [outputFramerate, setOutputFramerate] = useState(initial.output_framerate);
  const [outputBitrateKbps, setOutputBitrateKbps] = useState(initial.output_bitrate_kbps);
  const [outputQuality, setOutputQuality] = useState(initial.output_quality);
  const [outputUrlOverride, setOutputUrlOverride] = useState(
    initial.output_url_override ?? '',
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const body: Partial<Omit<MosaicSettings, 'stream_key_override_set'>> = {};
    if (targetGenital !== initial.mosaic_target_genital)
      body.mosaic_target_genital = targetGenital;
    if (targetBreast !== initial.mosaic_target_breast)
      body.mosaic_target_breast = targetBreast;
    if (detectionModel !== initial.detection_model) body.detection_model = detectionModel;

    const trimmedDir = trainingDataDir.trim();
    const initialDir = initial.training_data_dir ?? '';
    if (trimmedDir !== initialDir) {
      body.training_data_dir = trimmedDir || null;
    }
    if (outputWidth !== initial.output_width) body.output_width = outputWidth;
    if (outputHeight !== initial.output_height) body.output_height = outputHeight;
    if (outputFramerate !== initial.output_framerate)
      body.output_framerate = outputFramerate;
    if (outputBitrateKbps !== initial.output_bitrate_kbps)
      body.output_bitrate_kbps = outputBitrateKbps;
    if (outputQuality !== initial.output_quality) body.output_quality = outputQuality;

    const trimmedUrl = outputUrlOverride.trim();
    const initialUrl = initial.output_url_override ?? '';
    if (trimmedUrl !== initialUrl) {
      body.output_url_override = trimmedUrl || null;
    }

    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    try {
      await updateMutation.mutateAsync({ ulid, body });
      onClose();
    } catch {
      // 下で表示
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <fieldset
        style={{
          border: 'var(--border-default)',
          borderRadius: 'var(--radius-md)',
          padding: '12px 14px',
          margin: '0 0 16px',
        }}
      >
        <legend
          style={{
            padding: '0 6px',
            fontSize: '11px',
            color: 'var(--color-text-tertiary)',
          }}
        >
          検出対象
        </legend>
        <Toggle
          label="性器をモザイク処理する"
          checked={targetGenital}
          onChange={setTargetGenital}
        />
        <Toggle
          label="胸部をモザイク処理する"
          checked={targetBreast}
          onChange={setTargetBreast}
        />
      </fieldset>

      <Select
        label="検出モデル"
        value={detectionModel}
        onChange={(e) =>
          setDetectionModel(e.target.value as MosaicSettings['detection_model'])
        }
      >
        <option value="system_default">システム既定</option>
        <option value="user_custom">ユーザー個別学習</option>
      </Select>

      <Input
        label="学習データディレクトリ(空欄で未設定)"
        value={trainingDataDir}
        onChange={(e) => setTrainingDataDir(e.target.value)}
        placeholder="例: s3://safelens-training/perf-001/"
      />

      <fieldset
        style={{
          border: 'var(--border-default)',
          borderRadius: 'var(--radius-md)',
          padding: '12px 14px',
          margin: '0 0 16px',
        }}
      >
        <legend
          style={{
            padding: '0 6px',
            fontSize: '11px',
            color: 'var(--color-text-tertiary)',
          }}
        >
          出力設定
        </legend>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input
            label="幅 (px)"
            type="number"
            value={outputWidth}
            min={320}
            max={3840}
            onChange={(e) => setOutputWidth(Number(e.target.value))}
          />
          <Input
            label="高さ (px)"
            type="number"
            value={outputHeight}
            min={320}
            max={3840}
            onChange={(e) => setOutputHeight(Number(e.target.value))}
          />
          <Input
            label="フレームレート (fps)"
            type="number"
            value={outputFramerate}
            min={10}
            max={60}
            onChange={(e) => setOutputFramerate(Number(e.target.value))}
          />
          <Input
            label="ビットレート (kbps)"
            type="number"
            value={outputBitrateKbps}
            min={300}
            max={3000}
            onChange={(e) => setOutputBitrateKbps(Number(e.target.value))}
          />
        </div>
        <Select
          label="品質"
          value={outputQuality}
          onChange={(e) =>
            setOutputQuality(e.target.value as MosaicSettings['output_quality'])
          }
        >
          <option value="low">low</option>
          <option value="standard">standard</option>
          <option value="high">high</option>
        </Select>
      </fieldset>

      <Input
        label="出力先 URL 上書き(空欄で配信プロファイルの URL を使用)"
        value={outputUrlOverride}
        maxLength={500}
        onChange={(e) => setOutputUrlOverride(e.target.value)}
        placeholder="rtmp://example.com/live"
      />

      {updateMutation.error && (
        <p style={{ margin: '0 0 12px', fontSize: '11px', color: 'var(--color-error-text)' }}>
          {updateMutation.error instanceof ApiClientError
            ? updateMutation.error.message
            : '保存に失敗しました'}
        </p>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <div style={{ minWidth: 100 }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            キャンセル
          </Button>
        </div>
        <div style={{ minWidth: 100 }}>
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? '保存中…' : '保存'}
          </Button>
        </div>
      </div>
    </form>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        margin: '0 0 6px',
        fontSize: '12px',
        color: 'var(--color-text-secondary)',
        cursor: 'pointer',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}
